import { Button } from "@/components/ui/Button";
import { Loader2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useChatSession } from "../context/ChatSessionProvider";
import { useAutoScroll } from "../hooks/useAutoScroll";
import { TraceTurn } from "../types";
import { extractGroupCitations } from "../utils/citations";
import { isDatasetNote } from "../utils/datasetNote";
import { AssistantMessage } from "./AssistantMessage";
import { HomeHero } from "@/features/home/components/HomeHero";
import { useIsChatHome } from "../hooks/useIsChatHome";
import { UserMessage } from "./UserMessage";

interface MessageListProps {
  onExampleClick?: (query: string) => void;
  /** Narrow layout for side panels: tighter spacing, no home masthead. */
  compact?: boolean;
  /** Shown instead of the home masthead before the first message. */
  emptyState?: ReactNode;
}

export const MessageList = ({
  onExampleClick: _onExampleClick,
  compact = false,
  emptyState,
}: MessageListProps) => {
  const {
    sessionId,
    traceTurns,
    isLoading,
    isMessagesError,
    refetchMessages,
    isStreaming,
    isBackgroundRunning,
    hasMoreMessages,
    isLoadingOlder,
    loadOlderMessages,
  } = useChatSession();

  // A Datasets page note gets no reply, so it does not take the place of the
  // last user message that the thread scrolls to the top.
  const lastUserTurnId = useMemo(() => {
    const last = [...traceTurns]
      .reverse()
      .find((t) => t.type === "user" && !isDatasetNote(t.content));
    return last?.id ?? null;
  }, [traceTurns]);

  const userTurnCount = useMemo(
    () => traceTurns.filter((t) => t.type === "user" && !isDatasetNote(t.content)).length,
    [traceTurns],
  );
  const hasMultipleUserTurns = userTurnCount > 1;

  // Fresh chat with nothing sent yet shows the home masthead. On the first
  // send it fades out while the conversation rolls in underneath.
  const isHome = useIsChatHome() && emptyState === undefined;
  const [heroLeaving, setHeroLeaving] = useState(false);
  const wasHomeRef = useRef(isHome);
  useEffect(() => {
    if (wasHomeRef.current && !isHome) {
      setHeroLeaving(true);
      const t = window.setTimeout(() => setHeroLeaving(false), 480);
      wasHomeRef.current = isHome;
      return () => window.clearTimeout(t);
    }
    wasHomeRef.current = isHome;
  }, [isHome]);

  const [isPreservingScrollPosition, setIsPreservingScrollPosition] =
    useState(false);

  const {
    containerRef,
    scrollToBottom,
    suppressAutoScroll,
    pauseAutoScroll,
    resumeAutoScroll,
  } = useAutoScroll([traceTurns, isStreaming], {
    paused: isPreservingScrollPosition,
  });

  const prevLastUserTurnIdRef = useRef(lastUserTurnId);

  useLayoutEffect(() => {
    if (
      lastUserTurnId &&
      prevLastUserTurnIdRef.current &&
      lastUserTurnId !== prevLastUserTurnIdRef.current
    ) {
      suppressAutoScroll();
    }
  }, [lastUserTurnId, suppressAutoScroll]);

  useEffect(() => {
    if (
      lastUserTurnId &&
      prevLastUserTurnIdRef.current &&
      lastUserTurnId !== prevLastUserTurnIdRef.current
    ) {
      scrollToBottom();
    }
    prevLastUserTurnIdRef.current = lastUserTurnId;
  }, [lastUserTurnId, scrollToBottom]);

  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const [lastUserMessageHeight, setLastUserMessageHeight] = useState(0);

  useLayoutEffect(() => {
    if (lastUserMessageRef.current) {
      setLastUserMessageHeight(lastUserMessageRef.current.offsetHeight);
    }
  }, [lastUserTurnId]);

  const scrollAnchorRef = useRef<{
    scrollTop: number;
    scrollHeight: number;
  } | null>(null);

  const handleLoadOlder = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      scrollAnchorRef.current = {
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
      };
      pauseAutoScroll();
      setIsPreservingScrollPosition(true);
    }
    loadOlderMessages();
  }, [loadOlderMessages, containerRef, pauseAutoScroll]);

  // Re-apply scroll anchor as content height changes (prepended messages,
  // minHeight on last group, etc.) until loading finishes.
  useLayoutEffect(() => {
    const anchor = scrollAnchorRef.current;
    const container = containerRef.current;
    if (!anchor || !container) return;

    const delta = container.scrollHeight - anchor.scrollHeight;
    container.scrollTop = anchor.scrollTop + delta;

    if (!isLoadingOlder) {
      scrollAnchorRef.current = null;
      resumeAutoScroll();
      setIsPreservingScrollPosition(false);
    }
  }, [
    traceTurns,
    lastUserMessageHeight,
    hasMultipleUserTurns,
    isLoadingOlder,
    containerRef,
    resumeAutoScroll,
  ]);

  const messageGroups: Array<TraceTurn[]> = useMemo(() => {
    const groups: Array<TraceTurn[]> = [];
    let currentGroup: TraceTurn[] = [];

    for (const turn of traceTurns) {
      if (!currentGroup.length) {
        currentGroup.push(turn);
        continue;
      }

      const lastTurn = currentGroup[currentGroup.length - 1];
      if (lastTurn.type === turn.type) {
        currentGroup.push(turn);
        continue;
      }

      groups.push(currentGroup);
      currentGroup = [turn];
    }

    if (currentGroup.length) groups.push(currentGroup);
    return groups;
  }, [traceTurns]);

  /** Per assistant response group — scroll-back clicks use that group's map. */
  const groupCitationsById = useMemo(() => {
    const map = new Map<string, ReturnType<typeof extractGroupCitations>>();
    for (const group of messageGroups) {
      if (group[0]?.type !== "assistant") continue;
      map.set(group[0].id, extractGroupCitations(group));
    }
    return map;
  }, [messageGroups]);

  if (isLoading && !isStreaming) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (traceTurns.length === 0 && !isStreaming) {
    if (sessionId) {
      return (
        <div className="flex flex-1 items-center justify-center px-4">
          {isMessagesError ? (
            <div className="text-center">
              <p className="text-sm text-zinc-600">
                Could not load this conversation.
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetchMessages()}
                className="mt-3"
              >
                Try again
              </Button>
            </div>
          ) : (
            <p className="text-sm text-zinc-500">No messages in this session.</p>
          )}
        </div>
      );
    }

    if (emptyState !== undefined) {
      return <div className="flex-1 min-h-0 overflow-y-auto">{emptyState}</div>;
    }

    return (
      <div className="chat-home shrink-0">
        <div className="editorial-frame editorial-frame-home chat-home-col flex flex-col">
          <HomeHero />
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative flex-1 min-h-0 overflow-y-auto overscroll-contain"
      style={{ overflowAnchor: "auto" }}
    >
      {heroLeaving && (
        <div className="chat-home chat-home--leaving" aria-hidden>
          <div className="editorial-frame editorial-frame-home chat-home-col flex flex-col">
            <HomeHero />
          </div>
        </div>
      )}
      <div
        className={`chat-thread mx-auto max-w-3xl ${compact ? "px-4 py-4 space-y-5" : "px-4 py-6 space-y-6"}${heroLeaving ? " chat-rollout" : ""}`}
      >
        {hasMoreMessages && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLoadOlder}
            loading={isLoadingOlder}
            className="mx-auto block text-xs text-zinc-500 hover:text-zinc-700"
          >
            {isLoadingOlder ? "Loading…" : "Load older messages"}
          </Button>
        )}

        {messageGroups.map((group, groupIndex) => {
          const isLastGroup = groupIndex === messageGroups.length - 1;
          const hasAssistantTurns = group.some((t) => t.type !== "user");
          const onlyNotes = group.every((t) => t.type === "user" && isDatasetNote(t.content));
          return (
            <div
              key={group[0].id}
              className="group-container"
              {...(isLastGroup && hasAssistantTurns ? { "aria-live": "polite" as const } : {})}
              style={
                hasMultipleUserTurns && isLastGroup && !onlyNotes
                  ? {
                      minHeight: `calc(100dvh - ${compact ? 280 : 200}px - ${lastUserMessageHeight}px)`,
                    }
                  : undefined
              }
            >
              {group.map((turn, i) =>
                turn.type === "user" ? (
                  <div
                    key={turn.id}
                    ref={turn.id === lastUserTurnId ? lastUserMessageRef : undefined}
                    className={group[i + 1]?.type === "user" ? "mb-2" : undefined}
                  >
                    <UserMessage turn={turn} compact={compact} />
                  </div>
                ) : (
                  <AssistantMessage
                    key={turn.id}
                    turn={turn}
                    isLastInGroup={i === group.length - 1}
                    groupCitations={
                      groupCitationsById.get(group[0].id) ?? []
                    }
                  />
                )
              )}
            </div>
          );
        })}

        {isBackgroundRunning && !isStreaming && (
          <div role="status" className="flex items-center gap-2 px-4 py-2 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            <span>Agent is still working...</span>
          </div>
        )}
      </div>
    </div>
  );
};
